'use client';

import posthog from 'posthog-js';

/**
 * PostHog product analytics for the Aviado Research library.
 *
 * Joins the SAME PostHog project (375887, US cloud) as the Aviado mobile app,
 * so research-web persons stitch with app persons. The research site is the
 * FIRST web surface in the project — mobile arrives tagged `$os: iOS/Android`
 * via posthog-react-native; we stamp every research event with
 * `surface: research_web, platform: web` (super-property) so the two are
 * cleanly separable in every insight.
 *
 * Conventions ported from the mobile app (apps/mobile/src/lib):
 *  - snake_case event names, `*_viewed / *_clicked / *_completed` bookends
 *  - a typed `ResearchEvent` union (single source of truth, typo-proof)
 *  - a `before_send` PII scrubber
 *  - identify on the Supabase user UUID (NOT email) — matches mobile's
 *    distinct_id so a logged-in research session merges with the app person
 *
 * GA4 stays in place (dual-fired); PostHog is additive.
 */

// ---------------------------------------------------------------------------
// Event taxonomy — keep this union as the single source of truth.
// ---------------------------------------------------------------------------

export type ResearchEvent =
  // CTA module (the "which CTA converts" question) — all carry treatment/context/variant
  | 'cta_viewed'
  | 'beta_signup_clicked'
  | 'newsletter_submitted'
  | 'newsletter_succeeded'
  | 'newsletter_failed'
  // Cross-surface handoff — the funnel exit to the app
  | 'get_aviado_clicked'
  // Navigation graph
  | 'nav_link_clicked'
  | 'hub_tile_clicked'
  | 'cross_link_clicked'
  | 'recirculation_clicked'
  | 'search_submitted'
  | 'filter_applied'
  | 'filters_cleared'
  // Content engagement (bookends mirror mobile's article_* names)
  | 'article_opened'
  | 'outbound_clicked'
  // Audio
  | 'audio_play'
  | 'audio_pause'
  | 'audio_queue_add'
  | 'audio_queue_remove'
  | 'audio_skip'
  | 'dock_expanded';

type EventProps = Record<string, string | number | boolean | null | undefined>;

// ---------------------------------------------------------------------------
// PII scrubber (ported from mobile `sanitizeEvent`)
// ---------------------------------------------------------------------------

const REDACT_KEYS = new Set([
  'access_token',
  'age',
  'email',
  'first_name',
  'full_name',
  'last_name',
  'name_input',
  'push_token',
  'refresh_token',
]);

function scrub(properties: Record<string, unknown>) {
  for (const key of Object.keys(properties)) {
    if (REDACT_KEYS.has(key)) properties[key] = '[redacted]';
  }
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

let started = false;

export function initPostHog() {
  if (started || typeof window === 'undefined') return;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
  if (!key) return; // no key (e.g. dev without env) → no-op, never throws

  posthog.init(key, {
    api_host: host,
    debug: process.env.NEXT_PUBLIC_POSTHOG_DEBUG === '1',
    // We capture $pageview manually on App Router route changes (see provider);
    // disable the SDK's default to avoid double-counting.
    capture_pageview: false,
    capture_pageleave: true,
    // Lean, events-only setup matching the mobile app. autocapture off; we
    // capture named events explicitly. (Surveys/heatmaps are gated by
    // PostHog project remote-config — disable them in the project UI if their
    // loader scripts are unwanted; there's no stable init-config flag for it.)
    autocapture: false,
    disable_session_recording: true,
    person_profiles: 'identified_only',
    before_send: (event) => {
      if (event?.properties) scrub(event.properties);
      return event;
    },
    loaded: (ph) => {
      // Super-property on EVERY event — the web/mobile split the user asked for.
      ph.register({ surface: 'research_web', platform: 'web' });
    },
  });
  started = true;
}

// ---------------------------------------------------------------------------
// Capture + identify wrappers
// ---------------------------------------------------------------------------

export function capture(event: ResearchEvent, props?: EventProps) {
  if (typeof window === 'undefined' || !started) return;
  posthog.capture(event, props);
}

export function capturePageview(pathname: string) {
  if (typeof window === 'undefined' || !started) return;
  posthog.capture('$pageview', { $current_url: window.location.href, pathname });
}

/**
 * Identify on the Supabase user UUID (matches mobile's distinct_id). Call
 * when a logged-in session exists so the research person merges with the app
 * person. Research browsing is anonymous-first, so this usually no-ops here —
 * the bulk of identified activity arrives once PostHog lands on the web app's
 * auth path (separate repo).
 */
export function identifyResearchUser(supabaseUserId: string) {
  if (typeof window === 'undefined' || !started || !supabaseUserId) return;
  posthog.identify(supabaseUserId);
}

export function resetPostHog() {
  if (typeof window === 'undefined' || !started) return;
  posthog.reset();
}

export { posthog };
